import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Country } from './phone-number.models';
import { CountryCode } from 'libphonenumber-js';
import { defer, from, map, Observable, of } from 'rxjs';
import { isEqual } from 'lodash-es';
import { COUNTRIES_URL, COUNTRY_WITH_DIAL_CODE_STORAGE_KEY } from './constants';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class PhoneNumberService {
  protected _platformId = inject(PLATFORM_ID);
  protected _countriesUrl: string | null;

  constructor() {
    this._countriesUrl = inject(COUNTRIES_URL, {
      optional: true
    });
  }

  getCountries(
    preferredCountries?: Array<CountryCode> | null
  ): Observable<Country[]> {
    let countries$: Observable<Country[]>;
    const storedCountries = this._isPlatformBrowser
      ? sessionStorage.getItem(COUNTRY_WITH_DIAL_CODE_STORAGE_KEY)
      : null;

    if (storedCountries) {
      countries$ = of(JSON.parse(storedCountries) as Country[]);
    } else {
      countries$ = defer(() => {
        if (!this._countriesUrl) {
          return of([]);
        }

        return from(fetch(this._countriesUrl).then(res => res.json())).pipe(
          map(res => {
            if (typeof res === 'object' && 'countryList' in res) {
              if (this._isPlatformBrowser) {
                sessionStorage.setItem(
                  COUNTRY_WITH_DIAL_CODE_STORAGE_KEY,
                  JSON.stringify(res.countryList)
                );
              }
              return res.countryList;
            }
            return [];
          })
        );
      });
    }

    return countries$.pipe(
      map(countries => {
        if (preferredCountries?.length) {
          return countries.filter(country =>
            preferredCountries.includes(country.code)
          );
        }

        return countries;
      })
    );
  }

  getCountry(code: CountryCode): Observable<Country | undefined> {
    let countries$: Observable<Country[]>;
    const storedCountries = this._isPlatformBrowser
      ? sessionStorage.getItem(COUNTRY_WITH_DIAL_CODE_STORAGE_KEY)
      : null;

    if (storedCountries) {
      countries$ = of(JSON.parse(storedCountries) as Country[]);
    } else {
      countries$ = defer(() => {
        if (!this._countriesUrl) {
          return of([]);
        }

        return from(fetch(this._countriesUrl).then(res => res.json())).pipe(
          map(res => {
            if (typeof res === 'object' && 'countryList' in res) {
              if (this._isPlatformBrowser) {
                sessionStorage.setItem(
                  COUNTRY_WITH_DIAL_CODE_STORAGE_KEY,
                  JSON.stringify(res.countryList)
                );
              }
              return res.countryList;
            }
            return [];
          })
        );
      });
    }

    return countries$.pipe(
      map(countries => {
        return countries.find(country => isEqual(code, country.code));
      })
    );
  }

  protected get _isPlatformBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }
}
